import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AdminAnalyticsDto,
  AdminCustomerDto,
  AdminExtendResult,
  AssignSubscriptionRequest,
  CreateCustomerRequest,
  CreateCustomerResult,
  CreateStaffRequest,
  ExcelPreview,
  PagedResult,
  PriceCalculationDto,
  ProductImportMapping,
  ProductCheckResult,
  ProductImportResult,
  TenantStore,
  StaffDto,
  SubscriptionPlanDto,
  TenantPaymentDto,
  UpdateAdminCustomerRequest,
  UpdateStaffRequest,
} from '../models/admin.models';
import { FeedbackDto } from '../models/feedback.models';
import { CurrencySettingsDto } from '../models/currency.models';
import { BaseResponse } from '../models/auth.models';
import { ApiService } from './api.service';

@Injectable({ providedIn: 'root' })
export class AdminService extends ApiService {
  private adminUrl = environment.apiUrl + '/api/admin';
  private superAdminUrl = environment.apiUrl + '/api/superadmin';

  // ----- Customers -----

  getCustomers(params: {
    page?: number;
    pageSize?: number;
    search?: string;
    isActive?: boolean;
    subscriptionExpired?: boolean;
  }): Observable<BaseResponse<PagedResult<AdminCustomerDto>>> {
    let p = new HttpParams().set('page', params.page ?? 1).set('pageSize', params.pageSize ?? 20);
    if (params.search) p = p.set('search', params.search);
    if (params.isActive !== undefined) p = p.set('isActive', params.isActive);
    if (params.subscriptionExpired !== undefined)
      p = p.set('subscriptionExpired', params.subscriptionExpired);
    return this.http.get<BaseResponse<PagedResult<AdminCustomerDto>>>(
      `${this.adminUrl}/customers`,
      { params: p },
    );
  }

  createCustomer(data: CreateCustomerRequest): Observable<BaseResponse<CreateCustomerResult>> {
    return this.http.post<BaseResponse<CreateCustomerResult>>(`${this.adminUrl}/customers`, data);
  }

  updateCustomer(
    tenantId: string,
    data: UpdateAdminCustomerRequest,
  ): Observable<BaseResponse<void>> {
    return this.http.put<BaseResponse<void>>(`${this.adminUrl}/customers/${tenantId}`, data);
  }

  assignSubscription(
    tenantId: string,
    data: AssignSubscriptionRequest,
  ): Observable<BaseResponse<PriceCalculationDto>> {
    return this.http.put<BaseResponse<PriceCalculationDto>>(
      `${this.adminUrl}/customers/${tenantId}/subscription`,
      data,
    );
  }

  extendSubscription(
    tenantId: string,
    data: AssignSubscriptionRequest,
  ): Observable<BaseResponse<AdminExtendResult>> {
    return this.http.put<BaseResponse<AdminExtendResult>>(
      `${this.adminUrl}/customers/${tenantId}/subscription/extend`,
      data,
    );
  }

  getPayments(tenantId: string): Observable<BaseResponse<TenantPaymentDto[]>> {
    return this.http.get<BaseResponse<TenantPaymentDto[]>>(
      `${this.adminUrl}/customers/${tenantId}/payments`,
    );
  }

  // SuperAdmin: mijozni butunlay o'chirish (barcha ma'lumotlari bilan)
  deleteCustomer(tenantId: string): Observable<BaseResponse<void>> {
    return this.http.delete<BaseResponse<void>>(`${this.adminUrl}/customers/${tenantId}`);
  }

  // Mijozning do'konlari (import qoldig'i uchun)
  getTenantStores(tenantId: string): Observable<BaseResponse<TenantStore[]>> {
    return this.http.get<BaseResponse<TenantStore[]>>(
      `${this.adminUrl}/customers/${tenantId}/stores`,
    );
  }

  /**
   * MIJOZNING valyuta sozlamalari — import oynasidagi tanlagich uchun.
   *
   * Adminning o'z `CurrencyService` idan foydalanib bo'lmaydi: u admin hisobining
   * valyutalarini biladi, mijozniki esa boshqacha bo'lishi mumkin. Yoqilmagan kod
   * tanlansa backend importni rad etardi.
   */
  getCustomerCurrencies(tenantId: string): Observable<BaseResponse<CurrencySettingsDto>> {
    return this.http.get<BaseResponse<CurrencySettingsDto>>(
      `${this.adminUrl}/customers/${tenantId}/currencies`,
    );
  }

  /** Mijozning mavjud katalogini import qoidalari bo'yicha tekshirish (o'zgartirmaydi). */
  checkProducts(tenantId: string): Observable<BaseResponse<ProductCheckResult>> {
    return this.http.get<BaseResponse<ProductCheckResult>>(
      `${this.adminUrl}/customers/${tenantId}/products/check`,
    );
  }

  // 1-bosqich: Excel ustunlari va namuna qatorlarni o'qish
  previewProductImport(file: File): Observable<BaseResponse<ExcelPreview>> {
    const form = new FormData();
    form.append('file', file);
    return this.http.post<BaseResponse<ExcelPreview>>(
      `${this.adminUrl}/products/import/preview`,
      form,
    );
  }

  // 2-bosqich: ustun moslashtirish bo'yicha mahsulotlarni yaratish
  importProducts(
    tenantId: string,
    file: File,
    mapping: ProductImportMapping,
    storeId: string | null,
    currencies?: { sellCurrency: string | null; purchaseCurrency: string | null },
  ): Observable<BaseResponse<ProductImportResult>> {
    const form = new FormData();
    form.append('file', file);
    form.append('hasHeaderRow', String(mapping.hasHeaderRow));
    form.append('nameColumn', String(mapping.nameColumn ?? 0));
    form.append('sellPriceColumn', String(mapping.sellPriceColumn ?? 0));
    if (storeId) form.append('storeId', storeId);
    if (currencies?.sellCurrency) form.append('sellCurrency', currencies.sellCurrency);
    if (currencies?.purchaseCurrency) form.append('purchaseCurrency', currencies.purchaseCurrency);
    if (mapping.brandColumn != null) form.append('brandColumn', String(mapping.brandColumn));
    if (mapping.barcodeColumn != null) form.append('barcodeColumn', String(mapping.barcodeColumn));
    if (mapping.purchasePriceColumn != null)
      form.append('purchasePriceColumn', String(mapping.purchasePriceColumn));
    if (mapping.categoryColumn != null)
      form.append('categoryColumn', String(mapping.categoryColumn));
    if (mapping.unitColumn != null) form.append('unitColumn', String(mapping.unitColumn));
    if (mapping.isWeightColumn != null)
      form.append('isWeightColumn', String(mapping.isWeightColumn));
    if (mapping.quantityColumn != null)
      form.append('quantityColumn', String(mapping.quantityColumn));
    return this.http.post<BaseResponse<ProductImportResult>>(
      `${this.adminUrl}/customers/${tenantId}/products/import`,
      form,
    );
  }

  calculatePrice(planId: string, months: number): Observable<BaseResponse<PriceCalculationDto>> {
    return this.http.get<BaseResponse<PriceCalculationDto>>(`${this.adminUrl}/calculate-price`, {
      params: new HttpParams().set('subscriptionPlanId', planId).set('months', months),
    });
  }

  // ----- Analytics -----

  getAnalytics(params?: {
    dateFrom?: string;
    dateTo?: string;
    subscriptionPlanId?: string;
  }): Observable<BaseResponse<AdminAnalyticsDto>> {
    let p = new HttpParams();
    if (params?.dateFrom) p = p.set('dateFrom', params.dateFrom);
    if (params?.dateTo) p = p.set('dateTo', params.dateTo);
    if (params?.subscriptionPlanId) p = p.set('subscriptionPlanId', params.subscriptionPlanId);
    return this.http.get<BaseResponse<AdminAnalyticsDto>>(`${this.adminUrl}/analytics`, {
      params: p,
    });
  }

  // ----- Subscription Plans (read, for Admin/Operator) -----

  getSubscriptionPlans(): Observable<BaseResponse<SubscriptionPlanDto[]>> {
    return this.http.get<BaseResponse<SubscriptionPlanDto[]>>(
      `${this.adminUrl}/subscription-plans`,
    );
  }

  // ----- SuperAdmin: Subscription Plans CRUD -----

  getAllSubscriptionPlans(): Observable<BaseResponse<SubscriptionPlanDto[]>> {
    return this.http.get<BaseResponse<SubscriptionPlanDto[]>>(
      `${this.superAdminUrl}/subscription-plans`,
      { params: new HttpParams().set('includeArchived', true) },
    );
  }

  archivePlan(id: string, isArchived: boolean): Observable<BaseResponse<void>> {
    return this.http.patch<BaseResponse<void>>(
      `${this.superAdminUrl}/subscription-plans/${id}/archive?isArchived=${isArchived}`,
      {},
    );
  }

  createPlan(data: {
    name: string;
    nameRu?: string;
    nameEn?: string;
    nameCyrl?: string;
    description?: string;
    descriptionRu?: string;
    descriptionEn?: string;
    descriptionCyrl?: string;
    maxStores: number;
    maxEmployees: number;
    monthlyPrice: number;
    includedModules?: string[];
    displayOrder?: number;
    isView?: boolean;
  }): Observable<BaseResponse<{ id: string }>> {
    return this.http.post<BaseResponse<{ id: string }>>(
      `${this.superAdminUrl}/subscription-plans`,
      data,
    );
  }

  updatePlan(
    id: string,
    data: {
      name: string;
      nameRu?: string;
      nameEn?: string;
      nameCyrl?: string;
      description?: string;
      descriptionRu?: string;
      descriptionEn?: string;
      descriptionCyrl?: string;
      maxStores: number;
      maxEmployees: number;
      monthlyPrice: number;
      includedModules?: string[];
      displayOrder: number;
      isView?: boolean;
    },
  ): Observable<BaseResponse<void>> {
    return this.http.put<BaseResponse<void>>(`${this.superAdminUrl}/subscription-plans/${id}`, {
      id,
      ...data,
    });
  }

  togglePlan(id: string, isActive: boolean): Observable<BaseResponse<void>> {
    return this.http.patch<BaseResponse<void>>(
      `${this.superAdminUrl}/subscription-plans/${id}/status?isActive=${isActive}`,
      {},
    );
  }

  deletePlan(id: string): Observable<BaseResponse<void>> {
    return this.http.delete<BaseResponse<void>>(`${this.superAdminUrl}/subscription-plans/${id}`);
  }

  // ----- SuperAdmin: Discount CRUD -----

  createDiscount(
    planId: string,
    data: {
      description: string;
      minMonths: number;
      discountPercent: number;
    },
  ): Observable<BaseResponse<{ id: string }>> {
    return this.http.post<BaseResponse<{ id: string }>>(
      `${this.superAdminUrl}/subscription-plans/${planId}/discounts`,
      data,
    );
  }

  updateDiscount(
    id: string,
    data: {
      description: string;
      minMonths: number;
      discountPercent: number;
      isActive: boolean;
    },
  ): Observable<BaseResponse<void>> {
    return this.http.put<BaseResponse<void>>(`${this.superAdminUrl}/discounts/${id}`, data);
  }

  deleteDiscount(id: string): Observable<BaseResponse<void>> {
    return this.http.delete<BaseResponse<void>>(`${this.superAdminUrl}/discounts/${id}`);
  }

  // ----- Staff (SuperAdmin only) -----

  getStaff(params?: { search?: string; role?: number }): Observable<BaseResponse<StaffDto[]>> {
    let p = new HttpParams();
    if (params?.search) p = p.set('search', params.search);
    if (params?.role !== undefined) p = p.set('role', params.role);
    return this.http.get<BaseResponse<StaffDto[]>>(`${this.superAdminUrl}/staff`, { params: p });
  }

  createStaff(data: CreateStaffRequest): Observable<BaseResponse<{ id: string }>> {
    return this.http.post<BaseResponse<{ id: string }>>(`${this.superAdminUrl}/staff`, data);
  }

  updateStaff(id: string, data: UpdateStaffRequest): Observable<BaseResponse<void>> {
    return this.http.put<BaseResponse<void>>(`${this.superAdminUrl}/staff/${id}`, data);
  }

  // ----- Feedbacklar -----

  getFeedbacks(unreadOnly?: boolean): Observable<BaseResponse<FeedbackDto[]>> {
    let p = new HttpParams();
    if (unreadOnly !== undefined) p = p.set('unreadOnly', unreadOnly);
    return this.http.get<BaseResponse<FeedbackDto[]>>(`${this.adminUrl}/feedbacks`, { params: p });
  }

  getUnreadFeedbackCount(): Observable<BaseResponse<number>> {
    return this.http.get<BaseResponse<number>>(`${this.adminUrl}/feedbacks/unread-count`);
  }

  markFeedbackRead(id: string): Observable<BaseResponse<void>> {
    return this.http.put<BaseResponse<void>>(`${this.adminUrl}/feedbacks/${id}/read`, {});
  }
}
